export interface WidgetFeedbackContent {
  screenshotUrl?: string;
  fullpageUrl?: string;
  recordingUrl?: string;
  consoleLogs?: any[];
  networkLogs?: any[];
  actionLogs?: any[];
  metadata?: {
    comment?: string;
    url?: string;
    os?: string;
    browser?: string;
    windowSize?: string;
    timestamp?: string;
    country?: string;
    timezone?: string;
    [key: string]: any;
  };
}

export interface WidgetUploadKeys {
  screenshot?: string;
  fullpage?: string;
  recording?: string;
  console?: string;
  network?: string;
  actions?: string;
  metadata?: string;
}

export async function loadWidgetFeedback(
  uploads: WidgetUploadKeys,
  resolveKey: (key: string) => Promise<string | null>,
): Promise<WidgetFeedbackContent> {
  const content: WidgetFeedbackContent = {};

  const tasks: { type: string; key: string }[] = [];
  for (const [type, key] of Object.entries(uploads)) {
    if (key) tasks.push({ type, key });
  }

  const results = await Promise.allSettled(
    tasks.map(async ({ type, key }) => {
      const url = await resolveKey(key);
      if (!url) return { type, value: null };

      switch (type) {
        case "screenshot":
        case "fullpage":
          // Presigned URL used directly — no fetch needed
          return { type, value: url };

        case "recording": {
          // Fetch as blob for CORS-safe <video> playback
          const res = await fetch(url);
          if (!res.ok) return { type, value: null };
          const blob = await res.blob();
          return { type, value: URL.createObjectURL(blob) };
        }

        case "console":
        case "network":
        case "actions":
        case "metadata": {
          const res = await fetch(url);
          if (!res.ok) return { type, value: null };
          return { type, value: await res.json() };
        }

        default:
          return { type, value: null };
      }
    }),
  );

  for (const result of results) {
    if (result.status !== "fulfilled" || !result.value?.value) continue;
    const { type, value } = result.value;

    switch (type) {
      case "screenshot":
        content.screenshotUrl = value;
        break;
      case "fullpage":
        content.fullpageUrl = value;
        break;
      case "recording":
        content.recordingUrl = value;
        break;
      case "console":
        content.consoleLogs = value;
        break;
      case "network":
        content.networkLogs = value;
        break;
      case "actions":
        content.actionLogs = value;
        break;
      case "metadata":
        content.metadata = value;
        break;
    }
  }

  return content;
}

export function revokeWidgetFeedbackUrls(
  content: WidgetFeedbackContent | null,
): void {
  if (!content) return;
  // Only recording uses a blob URL; images use presigned URLs directly
  if (content.recordingUrl) URL.revokeObjectURL(content.recordingUrl);
}
